import { useTranslation } from 'react-i18next'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import ActionStatusTag from './ActionStatusTag'
import { formatDate } from '../../../lib/datetime'
import { CARD_VIEW_BREAKPOINT, useMediaQuery } from '../../../lib/useMediaQuery'
import type { SubAction } from '../types'

/**
 * Alt görev listesinin <b>salt-okuma, tıklanamaz</b> sürümü — "Ana Görevi Gör" popup'ında kullanılır
 * (kullanıcı isteği 2026-09-10: <i>"hepsini ekleyelim ama read only olsun gezinme yapamasın"</i>).
 *
 * <p><b>Neden {@code SubActionCards} / sayfadaki {@code DataTable} yeniden kullanılMADI:</b> ikisi de
 * <b>gezinmeyi yapısal olarak taşır</b> — kart {@code role="button"} + {@code ui-card--clickable} ile
 * doğar, tablo satırı {@code onRowClick} + {@code mtg-row-clickable} ile. Onlara boş bir {@code onOpen}
 * vermek satırları <b>tıklanabilir GÖSTERİR ama hiçbir şey yapmaz</b>: imleç el işareti olur, klavye
 * odağı satıra düşer, Enter sessizce yutulur. Bu, gezinmeyi kapatmaktan daha kötüdür (kırık bir
 * bağlantı hissi). Bu yüzden sunum kopyalanmadı, <b>etkileşimsiz</b> bir liste ayrıca yazıldı.
 *
 * <p>Kolonlar sayfadaki tabloyla aynıdır, <b>iki eksiğiyle</b>: satır tıklaması ve "Sil" kolonu. İkisi
 * de bu yüzeyde tanımsızdır — popup'ı açan kullanıcı ana görev üzerinde hiçbir işlem yapamaz.
 *
 * <p>Dar ekranda ({@code ≤CARD_VIEW_BREAKPOINT}) tablo yerine kart listesi çizilir — {@code
 * ActionNotesTable} ile aynı desen; dört kolon popup genişliğinde okunmuyor. Kartlar burada
 * <b>düz kutulardır</b>: {@code role}/{@code tabIndex} taşımazlar.
 *
 * <p><b>İsteğe bağlı gezinme — {@code onOpen} (kullanıcı isteği 02.10.2026).</b> Toplantı
 * Yönetimi'nin görev detay pop-up'ında alt görev satırına tıklanınca alt görev <b>yeni sekmede</b>
 * açılır: pop-up ve liste yerinde kalır, bağlam kaybolmaz. {@code onOpen} <b>verilmedikçe</b> liste
 * eskisi gibi etkileşimsizdir — "Ana Görevi Gör" ve Yeniden Açma pop-up'ları bu prop'u geçmez.
 * Verildiğinde yukarıdaki "tıklanabilir görünüp hiçbir şey yapmayan satır" sorunu doğmaz (tıklamanın
 * gerçek bir karşılığı vardır) ve biçim sayfadaki tablo/kartla aynıdır: satır tıklaması + başlıkta
 * gerçek bir {@code <button>} (klavye/ekran okuyucu), kartta overlay-link ({@code ui-card--linked}).
 */
export default function ReadOnlySubTaskList({
  subActions,
  showRecordNumber,
  onOpen,
}: Readonly<{
  subActions: SubAction[]
  /** Kayıt No kolonu — masaüstündeki `showRecordNumber` ile aynı yetki koşulu (RECORD_NUMBER_VIEW). */
  showRecordNumber: boolean
  /** Verilirse satırlar tıklanabilir olur ve alt görevi açar; verilmezse liste etkileşimsizdir. */
  onOpen?: (id: number) => void
}>) {
  const { t } = useTranslation()
  const isMobile = useMediaQuery(CARD_VIEW_BREAKPOINT)

  if (subActions.length === 0) {
    return <p className="act-empty-hint">{t('actions.detail.subActions.empty')}</p>
  }

  if (isMobile) {
    return (
      <div className="ui-card-list act-subaction-cards">
        {subActions.map((s) => (
          <div key={s.id} className={onOpen ? 'ui-card ui-card--linked' : 'ui-card'}>
            <div className="ui-card-head">
              {onOpen ? (
                <button
                  type="button"
                  className="ui-card-link"
                  onClick={() => onOpen(s.id)}
                  aria-label={t('actions.detail.subActions.openInNewTabNamed', { name: s.name })}
                >
                  <span className="ui-card-title">{s.name}</span>
                </button>
              ) : (
                <span className="ui-card-title">{s.name}</span>
              )}
              <ActionStatusTag statusId={s.statusId} name={s.statusName ?? ''} />
            </div>
            {showRecordNumber && s.recordNumber && (
              <span className="ui-card-sub">{s.recordNumber}</span>
            )}
            {s.description && <div className="ui-card-row act-card-desc">{s.description}</div>}
            {/* Bilgi satırı SubActionCards ile birebir: atanan + hedef teslim tarihi, tek satır. */}
            <div className="ui-card-row">
              {s.assignee?.fullName && <span>{s.assignee.fullName}</span>}
              {s.deadline && <span>{formatDate(s.deadline)}</span>}
            </div>
          </div>
        ))}
      </div>
    )
  }

  return (
    <DataTable
      value={subActions}
      dataKey="id"
      paginator={false}
      className="minutes-participant-table"
      // Satır tıklaması yalnız `onOpen` varken bağlanır. Başlıktaki <button>'a tıklama PrimeReact'te
      // satır tıklaması SAYILMAZ (tıklanabilir hedef elenir) → tek tıklama tek sekme açar.
      onRowClick={onOpen ? (e) => onOpen((e.data as SubAction).id) : undefined}
      rowClassName={onOpen ? () => 'mtg-row-clickable' : undefined}
    >
      {showRecordNumber && <Column header={t('actions.columns.number')} field="recordNumber" />}
      <Column
        header={t('actions.columns.name')}
        body={(s: SubAction) => {
          const name = (
            <span className="minutes-cell-strong act-cell-clip" title={s.name}>
              {s.name}
            </span>
          )
          return onOpen ? (
            <button
              type="button"
              className="act-cell-link"
              onClick={() => onOpen(s.id)}
              aria-label={t('actions.detail.subActions.openInNewTabNamed', { name: s.name })}
            >
              {name}
            </button>
          ) : (
            name
          )
        }}
      />
      <Column
        header={t('actions.detail.subActions.description')}
        body={(s: SubAction) => (
          <span className="act-cell-desc" title={s.description ?? ''}>
            {s.description ?? ''}
          </span>
        )}
      />
      <Column
        header={t('actions.detail.subActions.assignee')}
        body={(s: SubAction) => s.assignee?.fullName ?? ''}
      />
      <Column
        header={t('actions.columns.deadline')}
        body={(s: SubAction) => (s.deadline ? formatDate(s.deadline) : '')}
      />
      <Column
        header={t('actions.columns.status')}
        headerStyle={{ minWidth: '10rem' }}
        bodyStyle={{ minWidth: '10rem', whiteSpace: 'nowrap' }}
        body={(s: SubAction) => <ActionStatusTag statusId={s.statusId} name={s.statusName ?? ''} />}
      />
    </DataTable>
  )
}
